import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { deleteMemberships } from "./actions";
import ConfirmModal from "core/components/ConfirmModal";
import Error from "core/components/Error";
import Typography from "core/components/Typography";

const RemoveMemberConfirmModal = ({ member, isOpen, close, onSuccess }) => {
  const queryClient = useQueryClient();

  const [error, setError] = useState(null);

  const deleteMutationFn = useAuthenticatedMutation(deleteMemberships);
  const { mutate: removeMembership, isPending: isRemoving } = useMutation({
    mutationFn: deleteMutationFn,
    onError: (error, variables, onMutateResult, context) => {
      console.log("removeMembership.onError", { error });

      if (Array.isArray(error)) {
        setError(error[0]);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("There was an issue removing member.");
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      console.log("removeMembership.onSuccess", {
        data,
        variables,
        onMutateResult,
        context,
      });

      if (data.code === 207) {
        setError(
          data.results[0]?.error ?? "There was an issue removing member."
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: ["memberships"],
        });
        toast.success("Added member(s) successfully!");
        onSuccess?.();
      }
    },
  });

  const onNo = () => {
    setError(null);
    close();
  };

  const onYes = async () => {
    setError(null);
    removeMembership({
      memberships: [
        {
          user_id: member.user_id,
          organization_id: member.organization_id,
        },
      ],
    });
  };

  return (
    <ConfirmModal
      dataTest="remove-member-modal"
      visible={isOpen}
      dismiss={onNo}
      onNo={onNo}
      onYes={onYes}
      isSubmitting={isRemoving}
      title="Remove Member?"
      noLabel="Cancel"
      yesLabel="Remove"
      width="460px"
    >
      <Error>{error}</Error>
      <Typography variant="body" noMargin style={{ marginBottom: "16px" }}>
        Are you sure you want to remove{" "}
        <b>{member.name || member.email || "this user"}</b> from your
        organization? They will no longer be able to log in or access their data
        credentials for your organization.
      </Typography>
    </ConfirmModal>
  );
};

export default RemoveMemberConfirmModal;
